import React from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface ErrorDisplayProps {
  error: string | Error | null;
  onDismiss?: () => void;
  title?: string;
  variant?: 'error' | 'warning' | 'info';
  className?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onDismiss,
  title = 'Erreur',
  variant = 'error',
  className = ''
}) => {
  if (!error) return null;

  // Extraire le message d'erreur
  const getErrorMessage = (error: string | Error): string => {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error instanceof Error) {
      return error.message;
    }
    
    return 'Une erreur inattendue s\'est produite';
  };

  // Parser les erreurs de validation du serveur
  const parseServerErrors = (message: string): string[] => {
    try {
      // Essayer de parser comme JSON (erreurs de validation)
      const parsed = JSON.parse(message);
      
      if (typeof parsed === 'object' && parsed !== null) {
        const errors: string[] = [];
        
        // Parcourir les propriétés de l'objet d'erreur
        Object.entries(parsed).forEach(([field, fieldErrors]) => {
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach((error: string) => {
              errors.push(`${field}: ${error}`);
            });
          } else if (typeof fieldErrors === 'string') {
            errors.push(`${field}: ${fieldErrors}`);
          }
        });
        
        return errors.length > 0 ? errors : [message];
      }
    } catch {
      // Si ce n'est pas du JSON, retourner le message tel quel
    }
    
    return [message];
  };

  const errorMessage = getErrorMessage(error);
  const parsedErrors = parseServerErrors(errorMessage);

  // Styles selon la variante
  const getStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-800',
          text: 'text-yellow-700',
          dismissButton: 'text-yellow-400 hover:text-yellow-600'
        };
      case 'info':
        return {
          container: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          title: 'text-blue-800',
          text: 'text-blue-700',
          dismissButton: 'text-blue-400 hover:text-blue-600'
        };
      default: // error
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          title: 'text-red-800',
          text: 'text-red-700',
          dismissButton: 'text-red-400 hover:text-red-600'
        };
    }
  };

  const styles = getStyles();

  return (
    <div className={`rounded-lg border p-4 ${styles.container} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${styles.title}`}>
            {title}
          </h3>
          <div className={`mt-2 text-sm ${styles.text}`}>
            {parsedErrors.length === 1 ? (
              <p>{parsedErrors[0]}</p>
            ) : (
              <ul className="list-disc list-inside space-y-1">
                {parsedErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className={`inline-flex rounded-md p-1.5 ${styles.dismissButton} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600`}
              >
                <span className="sr-only">Fermer</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorDisplay;
